import React, { useState, useEffect } from "react";
import { Button, Space, Typography, Modal, Form, Input, InputNumber, DatePicker, Select, Tag, Tooltip, Alert, Tabs, Divider, Switch, Statistic, Row, Col } from "antd";
import { SectionCard, DataTable, StatusTag, EmptyState, InlineLoading } from "../../../../../components/shared";
import type { ColumnsType } from "antd/es/table";
import { PlusOutlined, DeleteOutlined, EditOutlined, BarcodeOutlined, TagOutlined, DownloadOutlined } from "@ant-design/icons";
import type { Product, ProductVariant } from "../../../../../types/inventory/ProductTypes";
import type { Stock, StockLabel, StockLabelDetail, ProductStockSummary, VariantStockSummaryEntry } from "../../../../../types/inventory/StockTypes";
import { 
	createStockEntry,
	updateStockEntry,
	deleteStockEntry,
	getProductStockSummary,
	fetchStockEntry,
	fetchProductLabels,
} from "../../../../../api/inventory/stockApi";
import { useNotifications } from "../../../../../context/NotificationContext";
import dayjs from "dayjs";

const { Title, Text } = Typography;

interface CommerceStockTabProps {
	product: Product;
}

const CommerceStockTab: React.FC<CommerceStockTabProps> = ({ product }) => {
	const [stockSummary, setStockSummary] = useState<ProductStockSummary | null>(null);
	const [loading, setLoading] = useState(false);
	const [addModalVisible, setAddModalVisible] = useState(false);
	const [editModalVisible, setEditModalVisible] = useState(false);
	const [selectedStock, setSelectedStock] = useState<Stock | null>(null);
	const [selectedVariantId, setSelectedVariantId] = useState<number | null>(null);
	const [generateLabels, setGenerateLabels] = useState(false);
	const [stockLabels, setStockLabels] = useState<StockLabel[]>([]);
	const [loadingLabels, setLoadingLabels] = useState(false);
	// ── Product-level label pool (cross-variant) ──────────────────────────
	const [productLabels, setProductLabels] = useState<StockLabelDetail[]>([]);
	const [labelPoolSummary, setLabelPoolSummary] = useState<{ total: number; available: number; assigned: number } | null>(null);
	const [loadingPoolLabels, setLoadingPoolLabels] = useState(false);
	const [labelPoolPage, setLabelPoolPage] = useState(1);
	const [labelPoolTotal, setLabelPoolTotal] = useState(0);
	const [labelPoolFilter, setLabelPoolFilter] = useState<"available" | "assigned" | undefined>();
	const [labelPoolOpen, setLabelPoolOpen] = useState(false);
	const [form] = Form.useForm();
	const [editForm] = Form.useForm();
	const { showError, showSuccess } = useNotifications();

	const variants = product.variants ?? [];
	const hasVariants = variants.length > 0;

	useEffect(() => {
		if (product.product_id) loadStockSummary();
	}, [product.product_id]);

	const loadStockSummary = async () => {
		setLoading(true);
		try {
			setStockSummary(await getProductStockSummary(product.product_id));
		} catch {
			showError("Failed to load stock information");
		} finally {
			setLoading(false);
		}
	};

	const handleAddStock = () => {
		form.resetFields();
		setGenerateLabels(false);
		if (hasVariants) {
			setSelectedVariantId(variants[0].id);
			form.setFieldValue("product_variant_id", variants[0].id);
		}
		setAddModalVisible(true);
	};

	const handleEditStock = async (stock: Stock, variantId: number) => {
		setSelectedStock(stock);
		setSelectedVariantId(variantId);
		editForm.setFieldsValue({
			quantity: stock.quantity,
			location: stock.location,
			batch_number: stock.batch_number,
			expiry_date: stock.expiry_date ? dayjs(stock.expiry_date) : null,
			status: stock.status ?? "available",
		});
		setEditModalVisible(true);
		loadStockLabels(stock.stock_id);
	};

	const handleDeleteStock = async (stockId: number) => {
		try {
			await deleteStockEntry(stockId);
			showSuccess("Stock entry deleted");
			loadStockSummary();
		} catch {
			showError("Failed to delete stock entry");
		}
	};

	const handleSaveNewStock = async () => {
		try {
			const values = await form.validateFields();
			const response = await createStockEntry({
				product_variant_id: values.product_variant_id,
				quantity: values.quantity,
				location: values.location,
				batch_number: values.batch_number,
				expiry_date: values.expiry_date?.format("YYYY-MM-DD"),
				status: values.status ?? "available",
				generate_labels: generateLabels,
				label_prefix: generateLabels ? values.label_prefix : undefined,
				label_suffix: generateLabels ? values.label_suffix : undefined,
				label_date_format: generateLabels ? values.label_date_format : undefined,
				label_increment_type: generateLabels ? values.label_increment_type : undefined,
			});
			showSuccess(generateLabels && response.labels_generated > 0 ? `Stock added with ${response.labels_generated} labels` : "Stock added");
			setAddModalVisible(false);
			loadStockSummary();
		} catch {
			showError("Failed to add stock entry");
		}
	};

	const handleUpdateStock = async () => {
		if (!selectedStock) return;
		try {
			const values = await editForm.validateFields();
			await updateStockEntry(selectedStock.stock_id, {
				quantity: values.quantity,
				location: values.location,
				batch_number: values.batch_number,
				expiry_date: values.expiry_date?.format("YYYY-MM-DD"),
				status: values.status,
			});
			showSuccess("Stock updated");
			setEditModalVisible(false);
			loadStockSummary();
		} catch {
			showError("Failed to update stock entry");
		}
	};

	const loadStockLabels = async (stockId: number) => {
		setLoadingLabels(true);
		setStockLabels([]);
		try {
			const detail = await fetchStockEntry(stockId);
			setStockLabels(detail.labels ?? []);
		} catch {
			showError("Failed to load labels");
		} finally {
			setLoadingLabels(false);
		}
	};

	const exportLabelsToCSV = () => {
		if (!stockLabels.length || !selectedStock) return;
		const variant = variants.find((v) => v.id === selectedVariantId);
		const rows = [
			["Label ID", "Status", "Assigned Date", "Created Date"],
			...stockLabels.map((l) => [l.label_id, l.is_assigned ? "Assigned" : "Available", l.assigned_at ? dayjs(l.assigned_at).format("YYYY-MM-DD HH:mm:ss") : "", dayjs(l.created_at).format("YYYY-MM-DD HH:mm:ss")]),
		];
		const csv = rows.map((r) => r.map((f) => `"${f}"`).join(",")).join("\n");
		const link = document.createElement("a");
		link.href = URL.createObjectURL(new Blob([csv], { type: "text/csv;charset=utf-8;" }));
		link.download = `labels_${variant?.sku ?? "unknown"}_${selectedStock.batch_number ?? "no_batch"}_${dayjs().format("YYYYMMDD_HHmmss")}.csv`;
		link.click();
		showSuccess("Labels exported");
	};

	const loadProductLabels = async (page = 1, filter?: "available" | "assigned") => {
		if (!product.product_id) return;
		setLoadingPoolLabels(true);
		try {
			const res = await fetchProductLabels(product.product_id, {
				status: filter,
				page,
				per_page: 20,
			});
			setProductLabels(res.data);
			setLabelPoolTotal(res.pagination.total_items);
			setLabelPoolPage(page);
			setLabelPoolSummary(res.summary);
		} catch {
			showError("Failed to load label pool");
		} finally {
			setLoadingPoolLabels(false);
		}
	};

	const exportPoolLabels = async () => {
		try {
			setLoadingPoolLabels(true);
			const res = await fetchProductLabels(product.product_id, { per_page: 500 });
			const rows = [
				["Label ID", "Status", "Variant", "Batch", "Assigned Date", "Created Date"],
				...res.data.map((l) => [
					l.label_id,
					l.is_assigned ? "Assigned" : "Available",
					l.stock?.variant?.sku ?? l.stock?.variant?.title ?? "",
					l.stock?.batch_number ?? "",
					l.assigned_at ? dayjs(l.assigned_at).format("YYYY-MM-DD HH:mm:ss") : "",
					dayjs(l.created_at).format("YYYY-MM-DD HH:mm:ss"),
				]),
			];
			const csv = rows.map((r) => r.map((f) => `"${f}"`).join(",")).join("\n");
			const link = document.createElement("a");
			link.href = URL.createObjectURL(new Blob([csv], { type: "text/csv;charset=utf-8;" }));
			link.download = `pool_labels_${product.sku ?? product.product_id}_${dayjs().format("YYYYMMDD_HHmmss")}.csv`;
			link.click();
			showSuccess("Pool labels exported");
		} catch {
			showError("Failed to export pool labels");
		} finally {
			setLoadingPoolLabels(false);
		}
	};

	const getStockStatusColor = (status: string) => ({ available: "success", reserved: "warning", assigned: "processing" })[status] ?? "default";

	const getVariantById = (id: number): ProductVariant | undefined => variants.find((v) => v.id === id);

	if (!hasVariants) {
		return (
			<EmptyState
				title="No Variants Found"
				description="This product needs at least one variant before stock can be managed. Variants are created automatically when syncing from Shopify."
			/>
		);
	}

	// Product-level totals from summary
	const totalAvailable = stockSummary?.total_available ?? 0;
	const totalCommitted = stockSummary?.total_committed ?? 0;
	const totalOnHand = stockSummary?.total_on_hand ?? 0;

	return (
		<div>
			<Space direction="vertical" style={{ width: "100%" }} size="large">
				{/* Product-level stock totals */}
				<Row gutter={16}>
					<Col xs={12} sm={6}>
						<SectionCard>
							<Statistic title={<Tooltip title="Units available to purchase (mirrors Shopify available)">Available</Tooltip>} value={totalAvailable} valueStyle={{ color: "var(--mg-success)" }} />
						</SectionCard>
					</Col>
					<Col xs={12} sm={6}>
						<SectionCard>
							<Statistic title={<Tooltip title="Ordered by customers but not yet shipped (mirrors Shopify committed)">Committed</Tooltip>} value={totalCommitted} valueStyle={{ color: "var(--mg-warning)" }} />
						</SectionCard>
					</Col>
					<Col xs={12} sm={6}>
						<SectionCard>
							<Statistic title={<Tooltip title="Physical units in warehouse = available + committed">On Hand</Tooltip>} value={totalOnHand} valueStyle={{ color: "var(--mg-primary)" }} />
						</SectionCard>
					</Col>
					<Col xs={12} sm={6}>
						<SectionCard>
							<Statistic
								title={<Tooltip title="Unassigned labels in the shared product pool (all variants draw from this)">Labels Available</Tooltip>}
								value={stockSummary?.available_labels ?? 0}
								suffix={`/ ${stockSummary?.total_labels ?? 0}`}
								prefix={<BarcodeOutlined />}
								valueStyle={{ color: "var(--mg-info)" }}
							/>
						</SectionCard>
					</Col>
				</Row>

				{/* Per-variant breakdown */}
				<SectionCard
					title="Inventory by Variant"
					extra={<Button type="primary" icon={<PlusOutlined />} onClick={handleAddStock}>Add Stock</Button>}
				>
					{loading ? (
						<InlineLoading message="Loading stock info…" minHeight={120} />
					) : stockSummary?.variants && stockSummary.variants.length > 0 ? (
						<Space direction="vertical" style={{ width: "100%" }} size="middle">
							{stockSummary.variants.map((vs: VariantStockSummaryEntry) => {
								const variant = getVariantById(vs.variant_id);
								const variantStocks: Stock[] = variant?.stock ?? [];

								return (
									<SectionCard
										key={vs.variant_id}
										title={
											<div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", width: "100%" }}>
												<Space>
													<Text strong>{vs.variant_title}</Text>
													<Tag>{vs.sku}</Tag>
													{(vs.labels_count ?? 0) > 0 && (
														<Tooltip title={`This variant's batches contributed ${vs.labels_count} labels to the shared pool (${vs.available_labels ?? 0} still unassigned from those batches)`}>
															<Tag icon={<BarcodeOutlined />} color={(vs.available_labels ?? 0) > 0 ? "geekblue" : "default"}>
																{vs.available_labels ?? 0}/{vs.labels_count} batch labels
															</Tag>
														</Tooltip>
													)}
												</Space>
												<Space size="large">
													<Tooltip title="Available to purchase">
														<Text>
															<Text type="secondary">Available: </Text>
															<Text style={{ fontSize: 16, color: "var(--mg-success)", fontWeight: "bold" }}>{vs.available}</Text>
														</Text>
													</Tooltip>
													<Tooltip title="Ordered, not yet shipped">
														<Text>
															<Text type="secondary">Committed: </Text>
															<Text style={{ fontSize: 16, color: "var(--mg-warning)", fontWeight: "bold" }}>{vs.committed}</Text>
														</Text>
													</Tooltip>
													<Tooltip title="Physical units in warehouse">
														<Text>
															<Text type="secondary">On Hand: </Text>
															<Text style={{ fontSize: 16, color: "var(--mg-primary)", fontWeight: "bold" }}>{vs.on_hand}</Text>
														</Text>
													</Tooltip>
												</Space>
											</div>
										}
									>
																				<DataTable dataSource={variantStocks} rowKey="stock_id" bare pagination={false} emptyTitle="No stock entries" columns={stockEntryColumns(vs.variant_id, handleEditStock, handleDeleteStock)} />
									</SectionCard>
								);
							})}
						</Space>
					) : (
						<EmptyState title="No stock entries found" />
					)}
				</SectionCard>

				{/* ── Product Label Pool ─────────────────────────────────── */}
				{(stockSummary?.total_labels ?? 0) > 0 && (
					<SectionCard
						title={
							<>
								<Space>
								<BarcodeOutlined style={{ color: "var(--mg-info)" }} />
								<Text strong style={{ fontSize: 14 }}>
									Product Label Pool
								</Text>
									<Tag color="purple">{stockSummary?.total_labels ?? 0} total</Tag>
									<Tag color="green">{stockSummary?.available_labels ?? 0} available</Tag>
									{(stockSummary?.assigned_labels ?? 0) > 0 && <Tag color="orange">{stockSummary?.assigned_labels ?? 0} assigned</Tag>}
								</Space>
								<Space>
									<Button
										size="small"
										icon={<TagOutlined />}
										onClick={() => {
											if (!labelPoolOpen) loadProductLabels(1, labelPoolFilter);
											setLabelPoolOpen((prev) => !prev);
										}}
									>
										{labelPoolOpen ? "Collapse" : "View Labels"}
									</Button>
									<Button size="small" icon={<DownloadOutlined />} onClick={exportPoolLabels}>
										Export CSV
									</Button>
								</Space>
							</>
						}
					>
						<Alert
							message="Labels are shared across all variants"
							description="All stock batches for every variant of this product contribute to one shared pool. When an order is placed — from Shopify or manually — labels are drawn from this pool regardless of which variant was ordered."
							type="info"
							showIcon
							style={{ marginBottom: labelPoolOpen ? 16 : 0 }}
						/>

						{labelPoolOpen && (
							<>
								<Space style={{ marginBottom: 12 }}>
									<Text type="secondary">Filter:</Text>
									<Tag.CheckableTag
										checked={labelPoolFilter === undefined}
										onChange={() => {
											setLabelPoolFilter(undefined);
											loadProductLabels(1, undefined);
										}}
									>
										All ({labelPoolSummary?.total ?? stockSummary?.total_labels ?? 0})
									</Tag.CheckableTag>
									<Tag.CheckableTag
										checked={labelPoolFilter === "available"}
										onChange={() => {
											setLabelPoolFilter("available");
											loadProductLabels(1, "available");
										}}
									>
										Available ({labelPoolSummary?.available ?? stockSummary?.available_labels ?? 0})
									</Tag.CheckableTag>
									<Tag.CheckableTag
										checked={labelPoolFilter === "assigned"}
										onChange={() => {
											setLabelPoolFilter("assigned");
											loadProductLabels(1, "assigned");
										}}
									>
										Assigned ({labelPoolSummary?.assigned ?? stockSummary?.assigned_labels ?? 0})
									</Tag.CheckableTag>
								</Space>

								<DataTable
									dataSource={productLabels}
									rowKey="id"
									loading={loadingPoolLabels}
									pagination={{
										current: labelPoolPage,
										pageSize: 20,
										total: labelPoolTotal,
										showSizeChanger: false,
										showTotal: (t) => `${t} labels`,
										onChange: (p) => loadProductLabels(p, labelPoolFilter),
									}}
									columns={[
										{
											title: "Label ID",
											dataIndex: "label_id",
											key: "label_id",
											render: (id: string) => (
												<Text code copyable={{ text: id }}>
													{id}
												</Text>
											),
										},
										{
											title: "Status",
											dataIndex: "is_assigned",
											key: "is_assigned",
											width: 110,
											render: (v: boolean) => <StatusTag status={v ? "assigned" : "active"} label={v ? "Assigned" : "Available"} />,
										},
										{
											title: "Variant",
											key: "variant",
											render: (_: any, r: StockLabelDetail) => <Text type="secondary">{r.stock?.variant?.sku ?? r.stock?.variant?.title ?? "—"}</Text>,
										},
										{
											title: "Batch",
											key: "batch",
											render: (_: any, r: StockLabelDetail) => (r.stock?.batch_number ? <Tag>{r.stock.batch_number}</Tag> : <Text type="secondary">—</Text>),
										},
										{
											title: "Assigned At",
											dataIndex: "assigned_at",
											key: "assigned_at",
											width: 160,
											render: (d: string) => (d ? dayjs(d).format("MMM D, YYYY HH:mm") : "—"),
										},
										{
											title: "Created",
											dataIndex: "created_at",
											key: "created_at",
											width: 140,
											render: (d: string) => dayjs(d).format("MMM D, YYYY"),
										},
									]}
								/>
							</>
						)}
					</SectionCard>
				)}
			</Space>

			{/* ── Add Stock Modal ─────────────────────────────────────────── */}
			<Modal
				title={
					<Space>
						<PlusOutlined />
						Add Stock Entry
					</Space>
				}
				open={addModalVisible}
				onCancel={() => setAddModalVisible(false)}
				onOk={handleSaveNewStock}
				width={700}
				okText="Add Stock"
			>
				<Form form={form} layout="vertical">
					<Row gutter={16}>
						<Col span={24}>
							<Form.Item label="Select Variant" name="product_variant_id" rules={[{ required: true }]}>
								<Select placeholder="Select variant" size="large" onChange={setSelectedVariantId}>
									{variants.map((v) => (
										<Select.Option key={v.id} value={v.id}>
											<Space>
												<Text>{v.formatted_title ?? v.title ?? "Default"}</Text>
												<Tag>{v.sku}</Tag>
											</Space>
										</Select.Option>
									))}
								</Select>
							</Form.Item>
						</Col>
						<Col span={12}>
							<Form.Item label="Quantity" name="quantity" rules={[{ required: true }]}>
								<InputNumber style={{ width: "100%" }} min={1} size="large" />
							</Form.Item>
						</Col>
						<Col span={12}>
							<Form.Item label="Status" name="status" initialValue="available">
								<Select size="large">
									<Select.Option value="available">Available</Select.Option>
									<Select.Option value="reserved">Reserved</Select.Option>
									<Select.Option value="assigned">Assigned</Select.Option>
								</Select>
							</Form.Item>
						</Col>
						<Col span={12}>
							<Form.Item label="Location" name="location" rules={[{ required: true }]}>
								<Input placeholder="Warehouse, Shelf, etc." />
							</Form.Item>
						</Col>
						<Col span={12}>
							<Form.Item label="Batch Number" name="batch_number">
								<Input />
							</Form.Item>
						</Col>
						<Col span={24}>
							<Form.Item label="Expiry Date" name="expiry_date">
								<DatePicker style={{ width: "100%" }} format="YYYY-MM-DD" />
							</Form.Item>
						</Col>

						<Col span={24}>
							<Divider />
							<Form.Item>
								<Space>
									<Switch checked={generateLabels} onChange={setGenerateLabels} />
									<Text strong>Generate Label IDs</Text>
									<Tooltip title="Labels share a product-wide pool — all variants of this product draw from the same set">
										<BarcodeOutlined style={{ color: "var(--mg-primary)" }} />
									</Tooltip>
								</Space>
							</Form.Item>
						</Col>

						{generateLabels && (
							<>
								<Col span={24}>
									<Alert
										message="Labels are shared across all variants of this product"
										description={`${form.getFieldValue("quantity") ?? 0} labels will be added to the product-level pool. Any variant's order can draw from it.`}
										type="info"
										showIcon
										icon={<TagOutlined />}
										style={{ marginBottom: 16 }}
									/>
								</Col>
								<Col span={12}>
									<Form.Item label="Prefix" name="label_prefix" tooltip="Text before the date and counter (e.g. 'SMT')">
										<Input placeholder="SMT" />
									</Form.Item>
								</Col>
								<Col span={12}>
									<Form.Item label="Suffix" name="label_suffix">
										<Input placeholder="Optional suffix" />
									</Form.Item>
								</Col>
								<Col span={12}>
									<Form.Item label="Date Format" name="label_date_format">
										<Select placeholder="Select format" allowClear>
											<Select.Option value="YYMM">YYMM (2601)</Select.Option>
											<Select.Option value="MMYY">MMYY (0126)</Select.Option>
											<Select.Option value="YYMMDD">YYMMDD (260130)</Select.Option>
											<Select.Option value="YYYYMMDD">YYYYMMDD (20260130)</Select.Option>
											<Select.Option value="DDMMYY">DDMMYY (300126)</Select.Option>
										</Select>
									</Form.Item>
								</Col>
								<Col span={12}>
									<Form.Item label="Increment Type" name="label_increment_type" initialValue="numeric">
										<Select>
											<Select.Option value="numeric">2-Digit (01, 02…)</Select.Option>
											<Select.Option value="numeric_4">4-Digit (0001, 0002…)</Select.Option>
											<Select.Option value="alpha">Alphabetic (A, B, C…)</Select.Option>
										</Select>
									</Form.Item>
								</Col>
							</>
						)}
					</Row>
				</Form>
			</Modal>

			{/* ── Edit Stock Modal ─────────────────────────────────────────── */}
			<Modal
				title={
					<Space>
						<EditOutlined />
						Edit Stock Entry
						{selectedStock?.batch_number && <Tag>{selectedStock.batch_number}</Tag>}
					</Space>
				}
				open={editModalVisible}
				onCancel={() => {
					setEditModalVisible(false);
					setStockLabels([]);
				}}
				onOk={handleUpdateStock}
				width={800}
				okText="Update Stock"
			>
				<Alert message="Label IDs cannot be modified after creation" type="info" showIcon style={{ marginBottom: 16 }} />

				<Tabs
					defaultActiveKey="details"
					items={[
						{
							key: "details",
							label: "Stock Details",
							children: (
								<Form form={editForm} layout="vertical">
									<Row gutter={16}>
										<Col span={12}>
											<Form.Item label="Quantity (Available)" name="quantity" rules={[{ required: true }]} tooltip="Units available to purchase — keep in sync with Shopify">
												<InputNumber style={{ width: "100%" }} min={0} size="large" />
											</Form.Item>
										</Col>
										<Col span={12}>
											<Form.Item label="Status" name="status">
												<Select size="large">
													<Select.Option value="available">Available</Select.Option>
													<Select.Option value="reserved">Reserved</Select.Option>
													<Select.Option value="assigned">Assigned</Select.Option>
												</Select>
											</Form.Item>
										</Col>
										<Col span={12}>
											<Form.Item label="Location" name="location" rules={[{ required: true }]}>
												<Input />
											</Form.Item>
										</Col>
										<Col span={12}>
											<Form.Item label="Batch Number" name="batch_number">
												<Input />
											</Form.Item>
										</Col>
										<Col span={24}>
											<Form.Item label="Expiry Date" name="expiry_date">
												<DatePicker style={{ width: "100%" }} format="YYYY-MM-DD" />
											</Form.Item>
										</Col>
									</Row>
								</Form>
							),
						},
						{
							key: "labels",
							label: (
								<Space>
									<BarcodeOutlined />
									{`Batch Labels (${stockLabels.length})`}
								</Space>
							),
							children: (
								<div>
									<Alert
										message="Labels are drawn from a product-wide pool"
										description="Any order for this product (any variant) will draw from the same label pool. This tab shows labels associated with this specific batch/stock entry."
										type="info"
										showIcon
										style={{ marginBottom: 16 }}
									/>

									{stockLabels.length > 0 && (
										<div style={{ marginBottom: 12, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
											<Space>
												<Text strong>Batch total: {stockLabels.length}</Text>
												<Tag color="green">Available: {stockLabels.filter((l) => !l.is_assigned).length}</Tag>
												<Tag color="orange">Assigned: {stockLabels.filter((l) => l.is_assigned).length}</Tag>
											</Space>
											<Button type="primary" icon={<DownloadOutlined />} onClick={exportLabelsToCSV} size="small">
												Export CSV
											</Button>
										</div>
									)}

									<DataTable
										dataSource={stockLabels}
										rowKey="id"
										loading={loadingLabels}
										bare
										pageSize={10}
										columns={[
											{
												title: "Label ID",
												dataIndex: "label_id",
												key: "label_id",
												width: 200,
												render: (id: string) => (
													<Text code copyable={{ text: id }}>
														{id}
													</Text>
												),
											},
											{
												title: "Status",
												dataIndex: "is_assigned",
												key: "is_assigned",
												width: 100,
												render: (v: boolean) => <StatusTag status={v ? "assigned" : "active"} label={v ? "Assigned" : "Available"} />,
											},
											{
												title: "Assigned",
												dataIndex: "assigned_at",
												key: "assigned_at",
												width: 150,
												render: (d: string) => (d ? dayjs(d).format("MMM D, YYYY HH:mm") : "—"),
											},
											{
												title: "Created",
												dataIndex: "created_at",
												key: "created_at",
												width: 150,
												render: (d: string) => dayjs(d).format("MMM D, YYYY HH:mm"),
											},
										]}
									/>

									{!stockLabels.length && !loadingLabels && <EmptyState title="No labels for this batch" />}
								</div>
							),
						},
					]}
				/>
			</Modal>
		</div>
	);
};

// ─── Stock entries table columns (extracted to keep JSX readable) ─────────────

function stockEntryColumns(variantId: number, onEdit: (s: Stock, vId: number) => void, onDelete: (id: number) => void): ColumnsType<Stock> {
	return [
		{
			title: <Tooltip title="Units available to purchase (mirrors Shopify available)">Available</Tooltip>,
			dataIndex: "quantity",
			key: "quantity",
			width: 100,
			render: (q: number) => (
				<Text strong style={{ color: "var(--mg-success)" }}>
					{q}
				</Text>
			),
		},
		{
			title: <Tooltip title="Units ordered but not yet shipped (mirrors Shopify committed)">Committed</Tooltip>,
			dataIndex: "committed_quantity",
			key: "committed_quantity",
			width: 100,
			render: (q: number) => (
				<Text strong style={{ color: q > 0 ? "var(--mg-warning)" : "var(--mg-border)" }}>
					{q ?? 0}
				</Text>
			),
		},
		{
			title: <Tooltip title="Physical units = available + committed">On Hand</Tooltip>,
			key: "on_hand",
			width: 100,
			render: (_: any, r: Stock) => (
				<Text strong style={{ color: "var(--mg-primary)" }}>
					{(r.quantity ?? 0) + (r.committed_quantity ?? 0)}
				</Text>
			),
		},
		{
			title: "Location",
			dataIndex: "location",
			key: "location",
			render: (v: string) => v ?? "—",
		},
		{
			title: "Batch",
			dataIndex: "batch_number",
			key: "batch_number",
			render: (v: string) => (v ? <Tag>{v}</Tag> : "—"),
		},
		{
			title: "Status",
			dataIndex: "status",
			key: "status",
			width: 100,
			render: (s: string) => <StatusTag status={s === "available" ? "active" : s === "reserved" ? "pending" : s as any} label={s ? s.charAt(0).toUpperCase() + s.slice(1) : s} />,
		},
		{
			title: "Expiry",
			dataIndex: "expiry_date",
			key: "expiry_date",
			width: 120,
			render: (d: string) => (d ? dayjs(d).format("MMM D, YYYY") : "—"),
		},
		{
			title: "Actions",
			key: "actions",
			width: 120,
			render: (_: any, record: Stock) => (
				<Space size="small">
					<Button type="link" size="small" icon={<EditOutlined />} onClick={() => onEdit(record, variantId)}>
						Edit
					</Button>
					<Button
						type="link"
						size="small"
						danger
						icon={<DeleteOutlined />}
						onClick={() => {
							Modal.confirm({
								title: "Delete this stock entry?",
								okText: "Delete",
								okType: "danger",
								onOk: () => onDelete(record.stock_id),
							});
						}}
					>
						Delete
					</Button>
				</Space>
			),
		},
	];
}

export default CommerceStockTab;
